/**
 * ניהול → מאזן — the one-time איזון משאבים reports.
 *
 * List (paid reports by default; drafts on demand), revenue, a detail dialog
 * with the case inputs + result, and the office actions: download the PDF,
 * resend it to the customer, regenerate a failed render. Also the price and the
 * expert block printed in every report. Full admins only (server-enforced).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, ExternalLink, RefreshCw, Send, Search, X } from 'lucide-react'
import api, { handleApiError } from '../../services/api'
import { useDialogA11y } from '../../hooks/useDialogA11y'
import { ils, STATUS_LABEL, type MazanInputs, type MazanStatus } from '../../features/mazan/model'
import { useAuthStore } from '../../store/authStore'
import { useSearchParams } from 'react-router-dom'
import { MazanCouponsCard, MazanNotifyCard, MazanUsersCard } from './MazanOpsCards'

interface Row {
  id: string
  status: MazanStatus
  customerName: string
  customerEmail: string
  priceIls?: number
  couponCode?: string
  headline?: { totalNet: number; valid: boolean }
  invoiceUrl?: string
  paidAt?: string
  generatedAt?: string
  emailedAt?: string
  generationError?: string
  createdAt: string
}

interface Detail {
  id: string
  status: MazanStatus
  customer: { name: string; email: string } | null
  inputs: MazanInputs
  summary: {
    valid: boolean
    blocks: string[]
    pension: { net: number; payer: 'a' | 'b' }
    capital: { net: number; payer: 'a' | 'b' }
    total: { net: number; payer: 'a' | 'b'; payee: 'a' | 'b' }
    partnershipYears: number | null
  }
  payment: { priceIls?: number; basePriceIls?: number; couponCode?: string; transactionId?: string; invoiceUrl?: string; paidAt?: string }
  delivery: { generatedAt?: string; emailedAt?: string; generationAttempts: number; generationError?: string; pdfBytes?: number; calcVersion?: string }
  consent: { dataConsentAt?: string; termsAcceptedAt?: string }
  contract: { signedAt: string; version: string; signerName?: string; customerEmailedAt?: string; officeEmailedAt?: string } | null
}

interface Settings {
  priceIls: number
  /** absent for a מנהל אזור — the expert block is the admin's */
  expert?: { name: string; title: string; idNumber: string; address: string; cv: string[]; licenses: string[] }
}

const fmt = (d?: string) => (d ? new Date(d).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—')

async function downloadPdf(id: string, what: 'pdf' | 'contract/pdf' = 'pdf') {
  const res = await api.get(`/admin/mazan/reports/${id}/${what}`, { responseType: 'blob' })
  const cd = String(res.headers['content-disposition'] || '')
  const m = cd.match(/filename\*=UTF-8''([^;]+)/)
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = m ? decodeURIComponent(m[1]) : what === 'pdf' ? 'mazan-report.pdf' : 'mazan-contract.pdf'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * Full admin: settings (price + expert), customers, coupons, and the reports.
 * מנהל אזור of מאזן: price, customers, coupons — never the reports (divorce-case
 * PII). The API enforces the same split; this only avoids rendering a 403.
 */
export default function MazanAdminTab() {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const [notice, setNotice] = useState('')
  return (
    <div className="space-y-6">
      <p role="status" aria-live="polite" className="sr-only">{notice}</p>
      {notice && <p className="rounded bg-emerald-50 p-2 text-sm text-emerald-950">{notice}</p>}
      <SettingsCard priceOnly={!isAdmin} onSaved={setNotice} />
      <MazanNotifyCard onNotice={setNotice} />
      <MazanUsersCard onNotice={setNotice} />
      <MazanCouponsCard onNotice={setNotice} />
      {isAdmin && <ReportsSection />}
    </div>
  )
}

function ReportsSection() {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [revenue, setRevenue] = useState<{ totalIls: number; count: number } | null>(null)
  const [statusFilter, setStatusFilter] = useState<'' | 'all' | MazanStatus>('')
  const [q, setQ] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  // ?report=<id> — the link in the new-report email opens that report directly
  const [searchParams] = useSearchParams()
  const [openId, setOpenId] = useState<string | null>(() => {
    const id = searchParams.get('report')
    return id && /^[a-f0-9]{24}$/i.test(id) ? id : null
  })

  const load = useCallback(() => {
    setError(null)
    api
      .get('/admin/mazan/reports', { params: { status: statusFilter || undefined, q: q || undefined } })
      .then((r) => {
        setRows(r.data.items)
        setRevenue(r.data.revenue)
      })
      .catch((e) => setError(handleApiError(e)))
  }, [statusFilter, q])

  useEffect(() => { load() }, [load])

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="mz-admin-list">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="mz-admin-list" className="text-lg font-semibold text-gray-900">דוחות איזון</h2>
            {revenue && (
              <p className="text-sm text-gray-700">סה״כ הכנסות מדוחות: <strong>{ils.format(revenue.totalIls)}</strong> · {revenue.count} תשלומים</p>
            )}
          </div>
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); load() }} role="search">
            <div>
              <label htmlFor="mz-admin-status" className="mb-1 block text-xs font-medium text-gray-700">סטטוס</label>
              <select id="mz-admin-status" className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
                <option value="">כל הדוחות ששולמו / בתהליך</option>
                <option value="generated">{STATUS_LABEL.generated}</option>
                <option value="paid">{STATUS_LABEL.paid}</option>
                <option value="failed">{STATUS_LABEL.failed}</option>
                <option value="pending_payment">{STATUS_LABEL.pending_payment}</option>
                <option value="draft">טיוטות</option>
                <option value="all">הכול כולל טיוטות</option>
              </select>
            </div>
            <div>
              <label htmlFor="mz-admin-q" className="mb-1 block text-xs font-medium text-gray-700">חיפוש לקוח (שם / מייל)</label>
              <input id="mz-admin-q" className="input" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-secondary inline-flex items-center gap-1"><Search size={16} aria-hidden="true" /> חיפוש</button>
          </form>
        </div>

        <p role="status" aria-live="polite" className="sr-only">{notice}</p>
        {notice && <p className="mt-3 rounded bg-emerald-50 p-2 text-sm text-emerald-950">{notice}</p>}
        {error && <p role="alert" className="mt-3 rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}

        {rows === null ? (
          <p role="status" className="mt-4 text-gray-700">טוען…</p>
        ) : rows.length === 0 ? (
          <p className="mt-4 text-gray-700">אין דוחות להצגה.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <caption className="sr-only">רשימת דוחות איזון</caption>
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">לקוח</th>
                  <th scope="col" className="px-3 py-2 text-start">סטטוס</th>
                  <th scope="col" className="px-3 py-2 text-start">שולם</th>
                  <th scope="col" className="px-3 py-2 text-start">סכום</th>
                  <th scope="col" className="px-3 py-2 text-start">נטו בדוח</th>
                  <th scope="col" className="px-3 py-2 text-start"><span className="sr-only">פעולות</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <span className="font-medium text-gray-900">{r.customerName}</span>
                      <span className="block text-xs text-gray-600" dir="ltr">{r.customerEmail}</span>
                    </td>
                    <td className="px-3 py-2">
                      {STATUS_LABEL[r.status]}
                      {r.status === 'generated' && !r.emailedAt && <span className="block text-xs text-amber-800">המייל טרם נשלח</span>}
                    </td>
                    <td className="px-3 py-2">{fmt(r.paidAt)}</td>
                    <td className="px-3 py-2">{r.priceIls != null ? ils.format(r.priceIls) : '—'}{r.couponCode ? <span className="block text-xs text-gray-600">קופון {r.couponCode}</span> : null}</td>
                    <td className="px-3 py-2">{r.headline?.valid ? ils.format(r.headline.totalNet) : '—'}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-primary-700 underline" onClick={() => setOpenId(r.id)}
                        aria-label={`פרטי הדוח של ${r.customerName}`}>פרטים</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openId && <DetailDialog id={openId} onClose={() => setOpenId(null)} onChanged={(msg) => { setNotice(msg); load() }} />}
    </div>
  )
}

function SettingsCard({ onSaved, priceOnly }: { onSaved: (msg: string) => void; priceOnly: boolean }) {
  const [s, setS] = useState<Settings | null>(null)
  const [price, setPrice] = useState('')
  const [expert, setExpert] = useState({ name: '', title: '', idNumber: '', address: '', cv: '', licenses: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get<Settings>('/admin/mazan/settings').then((r) => {
      setS(r.data)
      setPrice(String(r.data.priceIls || ''))
      // A מנהל אזור gets the price only — the expert block is the admin's.
      if (r.data.expert) setExpert({ ...r.data.expert, cv: r.data.expert.cv.join('\n'), licenses: r.data.expert.licenses.join('\n') })
    }).catch((e) => setError(handleApiError(e)))
  }, [])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const lines = (t: string) => t.split('\n').map((x) => x.trim()).filter(Boolean)
      const priceIls = Math.max(0, Math.round(Number(price) || 0))
      const r = await api.put<Settings>('/admin/mazan/settings', priceOnly
        ? { priceIls }
        : { priceIls, expert: { name: expert.name, title: expert.title, idNumber: expert.idNumber, address: expert.address, cv: lines(expert.cv), licenses: lines(expert.licenses) } })
      setS(r.data)
      onSaved(priceOnly ? 'המחיר נשמר' : 'ההגדרות נשמרו')
    } catch (e) {
      setError(handleApiError(e))
    } finally {
      setSaving(false)
    }
  }

  if (!s && !error) return <p role="status" className="text-gray-700">טוען הגדרות…</p>

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4" aria-labelledby="mz-admin-settings">
      <h2 id="mz-admin-settings" className="text-lg font-semibold text-gray-900">הגדרות איזון</h2>
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="mz-price" className="mb-1 block text-sm font-medium text-gray-800">מחיר דוח (₪, כולל מע״מ)</label>
          <input id="mz-price" type="number" min={0} step={1} className="input" value={price} onChange={(e) => setPrice(e.target.value)} aria-describedby="mz-price-hint" />
          <p id="mz-price-hint" className="mt-1 text-xs text-gray-600">0 = לא ניתן לרכוש עדיין. שינוי חל על רכישות חדשות בלבד.</p>
        </div>
        {!priceOnly && (<>
        <div>
          <label htmlFor="mz-exp-name" className="mb-1 block text-sm font-medium text-gray-800">עורך חוות הדעת</label>
          <input id="mz-exp-name" className="input" value={expert.name} onChange={(e) => setExpert({ ...expert, name: e.target.value })} />
        </div>
        <div>
          <label htmlFor="mz-exp-title" className="mb-1 block text-sm font-medium text-gray-800">תואר מקצועי</label>
          <input id="mz-exp-title" className="input" value={expert.title} onChange={(e) => setExpert({ ...expert, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="mz-exp-id" className="mb-1 block text-sm font-medium text-gray-800">ת.ז.</label>
            <input id="mz-exp-id" className="input" dir="ltr" value={expert.idNumber} onChange={(e) => setExpert({ ...expert, idNumber: e.target.value.replace(/\D/g, '') })} />
          </div>
          <div>
            <label htmlFor="mz-exp-addr" className="mb-1 block text-sm font-medium text-gray-800">כתובת המשרד</label>
            <input id="mz-exp-addr" className="input" value={expert.address} onChange={(e) => setExpert({ ...expert, address: e.target.value })} />
          </div>
        </div>
        <details className="md:col-span-2">
          <summary className="cursor-pointer text-sm font-medium text-gray-800">קורות חיים ורישיונות (שורה לכל סעיף)</summary>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="mz-exp-cv" className="mb-1 block text-sm text-gray-800">השכלה וניסיון מקצועי</label>
              <textarea id="mz-exp-cv" rows={8} className="input" value={expert.cv} onChange={(e) => setExpert({ ...expert, cv: e.target.value })} />
            </div>
            <div>
              <label htmlFor="mz-exp-lic" className="mb-1 block text-sm text-gray-800">רישיונות והסמכות</label>
              <textarea id="mz-exp-lic" rows={8} className="input" value={expert.licenses} onChange={(e) => setExpert({ ...expert, licenses: e.target.value })} />
            </div>
          </div>
        </details>
        </>)}
      </div>
      <button type="button" className="btn btn-primary mt-4" onClick={save} disabled={saving}>{saving ? 'שומר…' : priceOnly ? 'שמירת מחיר' : 'שמירת הגדרות'}</button>
    </section>
  )
}

function DetailDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: (msg: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [d, setD] = useState<Detail | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useDialogA11y(true, ref, onClose)

  const load = useCallback(() => {
    api.get<Detail>(`/admin/mazan/reports/${id}`).then((r) => setD(r.data)).catch((e) => setError(handleApiError(e)))
  }, [id])
  useEffect(() => { load() }, [load])

  const act = async (what: 'pdf' | 'contract' | 'resend' | 'regenerate') => {
    setBusy(what)
    setError(null)
    try {
      if (what === 'pdf') await downloadPdf(id)
      if (what === 'contract') await downloadPdf(id, 'contract/pdf')
      if (what === 'resend') { await api.post(`/admin/mazan/reports/${id}/resend`); onChanged('הדוח נשלח שוב ללקוח') }
      if (what === 'regenerate') {
        const r = await api.post(`/admin/mazan/reports/${id}/regenerate`)
        onChanged(r.data.status === 'generated' ? 'הדוח הופק מחדש ונשלח' : `ההפקה נכשלה: ${r.data.generationError || ''}`)
        load()
      }
    } catch (e) {
      setError(handleApiError(e))
    } finally {
      setBusy(null)
    }
  }

  const name = (s: 'a' | 'b') => (d ? d.inputs.people[s === 'a' ? 0 : 1].name : '')

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="mz-detail-title" className="my-8 w-full max-w-3xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <h2 id="mz-detail-title" className="text-lg font-semibold text-gray-900">פרטי דוח איזון</h2>
          <button type="button" onClick={onClose} aria-label="סגירה" className="rounded p-1 hover:bg-gray-100"><X size={20} aria-hidden="true" /></button>
        </div>
        <div className="space-y-5 p-4 text-sm">
          {error && <p role="alert" className="rounded bg-red-50 p-2 text-red-800">{error}</p>}
          {!d ? <p role="status">טוען…</p> : (
            <>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                <div><dt className="inline text-gray-600">לקוח: </dt><dd className="inline">{d.customer?.name} <span dir="ltr">({d.customer?.email})</span></dd></div>
                <div><dt className="inline text-gray-600">סטטוס: </dt><dd className="inline">{STATUS_LABEL[d.status]}</dd></div>
                <div><dt className="inline text-gray-600">הצדדים: </dt><dd className="inline">{name('a')} · {name('b')}</dd></div>
                <div><dt className="inline text-gray-600">תיק: </dt><dd className="inline">{d.inputs.court} {d.inputs.caseNo}</dd></div>
                <div><dt className="inline text-gray-600">נישואין / קרע: </dt><dd className="inline">{d.inputs.marriageDate} · {d.inputs.breakDate}</dd></div>
                <div><dt className="inline text-gray-600">שולם: </dt><dd className="inline">{fmt(d.payment.paidAt)} · {d.payment.priceIls != null ? ils.format(d.payment.priceIls) : '—'}{d.payment.couponCode ? ` (קופון ${d.payment.couponCode})` : ''}</dd></div>
                <div><dt className="inline text-gray-600">הופק: </dt><dd className="inline">{fmt(d.delivery.generatedAt)} · נשלח: {fmt(d.delivery.emailedAt)}</dd></div>
                <div><dt className="inline text-gray-600">הסכמה לשמירת נתונים: </dt><dd className="inline">{fmt(d.consent.dataConsentAt)}</dd></div>
                <div><dt className="inline text-gray-600">הסכם שירות: </dt><dd className="inline">{d.contract ? `נחתם ${fmt(d.contract.signedAt)}${d.contract.signerName ? ` · ${d.contract.signerName}` : ''}` : 'לא נחתם'}</dd></div>
              </dl>

              <div className="rounded-lg bg-emerald-50 p-3 text-emerald-950">
                {d.summary.valid ? (
                  <>
                    <p className="text-base font-semibold">נטו לתשלום: {ils.format(d.summary.total.net)} — {name(d.summary.total.payer)} ל{name(d.summary.total.payee)}</p>
                    <p>פנסיה: {ils.format(d.summary.pension.net)} · נכסים הוניים: {ils.format(d.summary.capital.net)} · שנות שותפות: {d.summary.partnershipYears?.toFixed(2)}</p>
                  </>
                ) : <p>התחשיב אינו תקף: {d.summary.blocks.join(', ')}</p>}
              </div>

              {d.inputs.people.map((p, i) => (
                <details key={i} className="rounded border border-gray-200 p-3">
                  <summary className="cursor-pointer font-medium">{i === 0 ? 'צד א׳' : 'צד ב׳'} — {p.name} (ת.ז. {p.idNumber || '—'}, {p.birth})</summary>
                  <ul className="mt-2 list-inside list-disc text-gray-800">
                    {p.funds.map((f, k) => <li key={`f${k}`}>קופה: {f.name} · {ils.format(f.amount)} לחודש · נפתחה {f.opened || '—'} · {f.body || '—'}</li>)}
                    {p.balances.map((b, k) => <li key={`b${k}`}>יתרה: {b.name} · {ils.format(b.amount)}</li>)}
                    {p.capital.map((c, k) => <li key={`c${k}`}>הוני: {c.name} · {ils.format(c.amount)}{c.liquid ? ' · נזיל' : ''}{c.atRetire ? ' · פנסיה לא פעילה' : ''}</li>)}
                  </ul>
                </details>
              ))}

              {d.delivery.generationError && (
                <p className="rounded bg-red-50 p-2 text-red-800">שגיאת הפקה ({d.delivery.generationAttempts} ניסיונות): {d.delivery.generationError}</p>
              )}

              <div className="flex flex-wrap gap-2 border-t pt-4">
                {d.status === 'generated' && (
                  <>
                    <button type="button" className="btn btn-primary inline-flex items-center gap-1" disabled={!!busy} onClick={() => act('pdf')}>
                      <Download size={16} aria-hidden="true" /> הורדת הדוח
                    </button>
                    <button type="button" className="btn btn-secondary inline-flex items-center gap-1" disabled={!!busy} onClick={() => act('resend')}>
                      <Send size={16} aria-hidden="true" /> שליחה חוזרת ללקוח
                    </button>
                  </>
                )}
                {['paid', 'failed', 'generated'].includes(d.status) && (
                  <button type="button" className="btn btn-outline inline-flex items-center gap-1" disabled={!!busy}
                    onClick={() => { if (d.status !== 'generated' || window.confirm('להפיק את הדוח מחדש ולשלוח שוב? הקובץ הקיים יוחלף.')) act('regenerate') }}>
                    <RefreshCw size={16} aria-hidden="true" /> {busy === 'regenerate' ? 'מפיק…' : 'הפקה מחדש'}
                  </button>
                )}
                {d.contract && (
                  <button type="button" className="btn btn-outline inline-flex items-center gap-1" disabled={!!busy} onClick={() => act('contract')}>
                    <Download size={16} aria-hidden="true" /> הסכם חתום
                  </button>
                )}
                {d.payment.invoiceUrl && (
                  <a className="btn btn-outline inline-flex items-center gap-1" href={d.payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={16} aria-hidden="true" /> חשבונית (CardCom)
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
